//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { useState } from 'react';

import { FileUploaderAssetFailure } from '@campaigns/components/shared/file-uploader/FileUploaderTypes';
import { MAX_IMAGE_SIZE_MB } from '@campaigns/constants/SamplingImageConstants';

interface UseImageUploadProps {
  onClearImage: () => void;
  onFileSelect: (file: File | undefined) => void;
}

export const useImageUpload = ({
  onClearImage,
  onFileSelect,
}: UseImageUploadProps) => {
  const [error, setError] = useState<string>();

  const handleFilesSelected = (
    files: File[],
    failureReasons: FileUploaderAssetFailure,
  ): void => {
    setError(undefined);

    if (failureReasons.isSizeTooLarge) {
      setError(`Please upload an image smaller than ${MAX_IMAGE_SIZE_MB}MB`);
      return;
    }

    if (failureReasons.isUnsupportedFilePresent) {
      setError('Please upload a valid image file');
      return;
    }

    if (failureReasons.isCountTooMany) {
      setError('Please upload only one image');
      return;
    }

    if (failureReasons.unknownReason) {
      setError('Please upload a valid image file');
      return;
    }

    const file = files[0];
    if (!file) {
      onClearImage();
      return;
    }

    onFileSelect(file);
  };

  return {
    error,
    handleFilesSelected,
    setError,
  };
};
